import { DatePicker, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import TotalAmountIcon from "../../../assets/icons/TotalAmountIcon";
import TotalDealersIcon from "../../../assets/icons/TotalDealersIcon";
import TotalTransactionsIcon from "../../../assets/icons/TotalTransactionsIcon";
import Loader from "../../../components/ui/Loader";
import {
  useGetOverviewQuery,
  useGetTopPartiesQuery,
} from "../../../features/bsrm/dashboard/dashboardApi";
import { useGetSingleCompanyQuery } from "../../../features/portal/companyOnboard/companyOnboardApi";
import useError from "../../../hooks/useError";
import useSelect from "../../../hooks/useSelect";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatSouthAsianNumber } from "../../../utils/utils";
import TopDealers from "./TopDealers";
import TopRetailers from "./TopRetailers";
import TopTraders from "./TopTraders";
import TransactionsChart from "./TransactionsChart";

type MerchantProfile = {
  merchantNumber: string;
  merchantName: string;
  profileImage: string | null;
  logoImage: string | null;
  businessName?: string; // present in suppliers.profile
};

type SupplierInfo = {
  dealerCode: number;
  district: string;
  division: string;
  zone: string;
  thana: string;
  name: string;
  merchantMsisdn: string;
  profile: MerchantProfile;
  businessUnit: any; // you can replace `any` with actual type if known
};

type TraderItem = {
  id: string;
  traderTitle: string;
  supplierId: string;
  businessUnitId: string;
  distributorId: string;
  distributorTitle: string;
  traderMsisdn: string;
  distributorMsisdn: string;
  requestFrom: string;
  requestTo: string;
  status: string;
  note: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  profile: MerchantProfile;
  suppliers: SupplierInfo;
  tradersToSupplierTransactionCount: number;
  tradersToDealerTransactionCount: number;
  tradersToSupplierTransactionAmount: number;
  tradersToDealerTransactionAmount: number;
  totalTransactionCount: number;
  totalTransactionAmount: number;
};

export default function BsrmDashboard() {
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("overall");
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [activeButton, setActiveButton] = useState("dealers");
  const [selectedSupplierId, setSelectedSupplierId] = useState<
    string | undefined
  >(undefined);
  const [rangeValue, setRangeValue] = useState<
    [Dayjs | null, Dayjs | null] | null
  >([dayjs().startOf("day"), null]);
  // const [rangeValue, setRangeValue] = useState<
  //   [Dayjs | null, Dayjs | null] | null
  // >([
  //   dayjs().startOf("month"), // Start: 1st of current month
  //   dayjs().endOf("month"), // End: last day of current month
  // ]);
  const [weekValue, setWeekValue] = useState<Dayjs | null>(null);
  const [monthValue, setMonthValue] = useState<Dayjs | null>(dayjs());

  const { RangePicker } = DatePicker;
  const category =
    selectedCategory === "overall" ? activeButton : selectedCategory;
  const [search] = useSearchParams();

  const {
    dateFilter: topDealersDate,
    select: topDealersSelect,
    handleDateChange,
  } = useSelect("today");

  const handleSelectChange = (value: any) => {
    setSelectedCategory(value);
  };

  const handleSelectChangePeriod = (value: any) => {
    setSelectedPeriod(value);
  };

  // useEffect(() => {
  //   const today = dayjs().startOf("day");
  //   setRangeValue([today, null]); // Only start date
  //   handleDateChange([today, null]); // Send to hook
  // }, []);

  useEffect(() => {
    const startOfMonth = dayjs().startOf("month");
    const endOfMonth = dayjs().endOf("month");

    setRangeValue([startOfMonth, endOfMonth]);
    handleDateChange([startOfMonth, endOfMonth]);
  }, []);

  const { user } = useAppSelector((state) => state.auth);
  const {
    isError: isCompanyError,
    error: companyError,
    isLoading: isCompanyLoading,
    data: companyData,
  } = useGetSingleCompanyQuery(user?.data?.company_id || "", {
    skip: !user?.data?.company_id,
  });

  const selectedSupplier = companyData?.data?.dynamicKeys?.find(
    (item: any) => item.id === Number(selectedSupplierId)
  );

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    error: overviewError,
  } = useGetOverviewQuery(
    {
      params: {
        ...topDealersDate,
        // supplierId: selectedSupplier?.dynamic_id,
        supplierKeyword: String(selectedSupplier?.dynamic_keyword),
        period: selectedPeriod,
      },
    },
    {
      skip: loading,
    }
  );

  // const { data: topDealersData, isLoading: isTopDealersDataLoading } =
  //   useGetTopDealersQuery(
  //     {
  //       params: {
  //         ...topDealersDate,
  //         supplierId: selectedSupplier?.dynamic_id,
  //         supplierKeyword: selectedSupplier?.dynamic_keyword,
  //       },
  //     },
  //     {
  //       skip: loading,
  //     }
  //   );

  // const { data: topTradersData, isLoading: isTopTradersLoading } =
  //   useGetDashboardTopTradersQuery(
  //     {
  //       ...topDealersDate,
  //       supplierId: selectedSupplier?.dynamic_id,
  //       supplierKeyword: selectedSupplier?.dynamic_keyword,
  //     },
  //     {
  //       skip: loading,
  //     }
  //   );

  // const { data: treaders, isLoading: treadersLoading } = useGetTraderListQuery(
  //   {
  //     ...topDealersDate,
  //     supplierId: selectedSupplier?.dynamic_id,
  //     supplierKeyword: selectedSupplier?.dynamic_keyword,
  //     limit: 1000,
  //   },
  //   {
  //     skip: loading,
  //   }
  // );

  const partyType =
    activeButton === "dealers"
      ? "dealer"
      : activeButton === "traders"
      ? "trader"
      : activeButton === "retailers"
      ? "retailer"
      : activeButton;

  const {
    data: getTopPartiesData,
    isError: getTopPartiesIsError,
    error: getTopPartiesError,
    isLoading: getTopPartiesIsLoading,
  } = useGetTopPartiesQuery(
    {
      params: {
        ...topDealersDate,
        supplierId: selectedSupplier?.dynamic_id,
        // supplierKeyword: selectedSupplier?.dynamic_keyword,
        party: partyType,
      },
    },
    {
      skip: loading,
    }
  );

  // console.log("selectedSupplier?.dynamic_id:", selectedSupplier?.dynamic_id);
  // console.log(
  //   "selectedSupplier?.dynamic_keyword:",
  //   selectedSupplier?.dynamic_keyword
  // );

  // const uniqueMerchantCount = treaders?.data?.rows?.reduce((set, item) => {
  //   if (item.profile?.merchantNumber) {
  //     set.add(item.profile.merchantNumber);
  //   }
  //   if ((item as unknown as TraderItem)?.profile?.merchantNumber) {
  //     set.add((item as unknown as TraderItem).profile.merchantNumber);
  //   }
  //   return set;
  // }, new Set<string>()).size;

  // function totalTreaderAmount(item: keyof TraderDetailsData) {
  //   let totalAmount;
  //   totalAmount = formatSouthAsianNumber(
  //     treaders?.data?.rows?.reduce((acc, curr) => {
  //       return acc + Number(curr?.[item]) || 0;
  //     }, 0) || 0
  //   );

  //   return totalAmount;
  // }

  useError(isOverviewError, overviewError, "Get overview failed");
  useError(isCompanyError, companyError, "Company get fail");
  useError(
    getTopPartiesIsError,
    getTopPartiesError,
    "Get top parties data failed"
  );

  const getTopTitle = () => {
    if (selectedCategory === "dealers") {
      return "Top 10 Dealers";
    }
    if (selectedCategory === "traders") {
      return "Top 10 Traders";
    }
    if (selectedCategory === "retailers") {
      return "Top 10 Retailers";
    }
    if (selectedCategory === "overall") {
      if (activeButton === "dealers") return "Top 10 Dealers";
      if (activeButton === "traders") return "Top 10 Traders";
      if (activeButton === "retailers") return "Top 10 Retailers";
    }
    return "";
  };

  const overviewDealersCardsData = [
    {
      id: 1,
      icon: TotalDealersIcon,
      label: "Total Active Dealers",
      amount: overviewData?.data?.dealer?.total || 0,
    },
    {
      id: 2,
      icon: TotalAmountIcon,
      label: "Total Amount of Deals",
      amount: overviewData?.data?.dealer?.totalAmount || 0,
    },
    {
      id: 3,
      icon: TotalTransactionsIcon,
      label: "Total Transactions",
      amount: overviewData?.data?.dealer?.totalTransactions || 0,
    },
  ];

  const overviewTreaderCardsData = [
    {
      id: 1,
      icon: TotalDealersIcon,
      label: "Total Active Traders",
      amount: overviewData?.data?.trader?.total || 0,
    },
    {
      id: 2,
      icon: TotalAmountIcon,
      label: "Total Amount of Deals",
      amount: overviewData?.data?.trader?.totalAmount || 0,
    },
    {
      id: 3,
      icon: TotalTransactionsIcon,
      label: "Total Transactions",
      amount: overviewData?.data?.trader?.totalTransactions || 0,
    },
  ];

  const overviewRetailerCardsData = [
    {
      id: 1,
      icon: TotalDealersIcon,
      label: "Total Active Retailers",
      amount: overviewData?.data?.retailer?.total || 0,
    },
    {
      id: 2,
      icon: TotalAmountIcon,
      label: "Total Amount of Deals",
      amount: overviewData?.data?.retailer?.totalAmount || 0,
    },
    {
      id: 3,
      icon: TotalTransactionsIcon,
      label: "Total Transactions",
      amount: overviewData?.data?.retailer?.totalTransactions || 0,
    },
  ];

  const renderTopContent = () => {
    if (category === "dealers") {
      return (
        <TopDealers
          dates={topDealersDate}
          isLoading={getTopPartiesIsLoading}
          getTopPartiesData={getTopPartiesData}
        />
      );
    }

    if (category === "traders") {
      return (
        <TopTraders
          isLoading={getTopPartiesIsLoading}
          data={getTopPartiesData}
        />
      );
    }

    if (category === "retailers") {
      return (
        <TopRetailers
          isLoading={getTopPartiesIsLoading}
          data={getTopPartiesData}
        />
      );
    }

    return null;
  };

  useEffect(() => {
    const type = search.get("type");
    const dynamicId = search.get("dynamic-id");
    const dynamicKeyword = search.get("dynamic-keyword");

    if (type) {
      localStorage.setItem("product-type", JSON.stringify(type));
    }

    if (dynamicId && dynamicKeyword) {
      localStorage.setItem(
        "dynamic",
        JSON.stringify({
          dynamicId,
          dynamicKeyword,
        })
      );
    }

    const t = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const dynamicKeys = companyData?.data?.dynamicKeys;
    if (Array.isArray(dynamicKeys) && dynamicKeys.length > 0) {
      const firstId = String(dynamicKeys[0].id);
      setSelectedSupplierId((prev) => prev ?? firstId);
    }
  }, [companyData]);

  // useEffect(() => {
  //   const type = search.get("type");

  //   // Prefer selectedSupplier first, fallback to search params
  //   const dynamicId =
  //     createHash(String(selectedSupplier?.dynamic_id)) ||
  //     search.get("dynamic-id");
  //   const dynamicKeyword =
  //     createHash(String(selectedSupplier?.dynamic_keyword)) ||
  //     search.get("dynamic-keyword");

  //   if (type) {
  //     localStorage.setItem("product-type", JSON.stringify(type));
  //   }

  //   if (dynamicId && dynamicKeyword) {
  //     localStorage.setItem(
  //       "dynamic",
  //       JSON.stringify({
  //         dynamicId,
  //         dynamicKeyword,
  //       })
  //     );
  //   }

  //   const t = setTimeout(() => {
  //     setLoading(false);
  //   }, 500);

  //   return () => clearTimeout(t);
  // }, [search, selectedSupplier?.dynamic_id, selectedSupplier?.dynamic_keyword]);

  return (
    <>
      <SidebarLayout title="Dashboard">
        {loading || isCompanyLoading || isOverviewLoading ? (
          <Loader />
        ) : (
          <div>
            <div className="flex flex-wrap lg:flex-nowrap justify-between items-center gap-4 mb-6">
              <div className="flex justify-start items-center gap-3">
                <div className="self-stretch justify-start !text-[#2D3643] text-4xl font-medium ">
                  Overview
                </div>

                <Select
                  placeholder="Select Supplier Key"
                  showSearch
                  className="!min-w-[110px]"
                  suffixIcon={<SelectDownIcon />}
                  style={{ width: "100%" }}
                  // allowClear
                  optionFilterProp="label"
                  options={companyData?.data?.dynamicKeys?.map((item: any) => ({
                    label: item.dynamic_key_name,
                    value: String(item.id),
                  }))}
                  value={selectedSupplierId} // 👈 controlled value
                  onChange={(value) => setSelectedSupplierId(value)}
                />

                <Select
                  className="!min-w-[100px]"
                  showSearch
                  suffixIcon={<SelectDownIcon />}
                  placeholder="Select a person"
                  optionFilterProp="label"
                  // allowClear
                  defaultValue="overall"
                  onChange={handleSelectChange}
                  options={[
                    { value: "overall", label: "Overall" },
                    { value: "dealers", label: "Dealers" },
                    { value: "traders", label: "Traders" },
                    { value: "retailers", label: "Retailers" },
                  ]}
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex flex-wrap lg:flex-nowrap items-center gap-2">
                  <RangePicker
                    allowClear
                    format="YYYY-MM-DD"
                    value={rangeValue} // ✅ bind value
                    onChange={(dates: any) => {
                      setWeekValue(null);
                      setMonthValue(null);
                      if (!dates || dates.length !== 2) {
                        setRangeValue(null);
                        // window.location.reload();
                        return;
                      }
                      const start = dates[0].startOf("day");
                      const end = dates[1].endOf("day");
                      setRangeValue([start, end]); // ✅ update state
                      handleDateChange([start, end]);
                    }}
                    placeholder={["Start Date", "End Date"]}
                  />

                  <DatePicker
                    allowClear
                    picker="week"
                    value={weekValue} // ✅ bind value
                    onChange={(date: Dayjs | null) => {
                      setRangeValue(null);
                      setMonthValue(null);

                      if (!date) {
                        setWeekValue(null);
                        handleDateChange(undefined);
                        return;
                      }

                      const startOfWeek = date.startOf("week");
                      const endOfWeek = date.endOf("week");

                      setWeekValue(date);
                      setRangeValue([startOfWeek, endOfWeek]); // ✅ Reflect week in RangePicker
                      handleDateChange([startOfWeek, endOfWeek]);
                    }}
                  />

                  <DatePicker
                    allowClear
                    picker="month"
                    value={monthValue} // ✅ bind value
                    onChange={(date: Dayjs | null) => {
                      setRangeValue(null);
                      setWeekValue(null);

                      if (!date) {
                        setMonthValue(null);
                        handleDateChange(undefined);
                        return;
                      }

                      const startOfMonth = date.startOf("month");
                      const endOfMonth = date.endOf("month");

                      setMonthValue(date);
                      setRangeValue([startOfMonth, endOfMonth]); // ✅ Reflect month in RangePicker
                      handleDateChange([startOfMonth, endOfMonth]);
                    }}
                  />

                  <Select
                    className="!min-w-[100px]"
                    showSearch
                    suffixIcon={<SelectDownIcon />}
                    placeholder="Select Period"
                    optionFilterProp="label"
                    // allowClear
                    defaultValue="week"
                    onChange={handleSelectChangePeriod}
                    options={[
                      { value: "week", label: "Week" },
                      { value: "month", label: "Month" },
                      { value: "year", label: "Year" },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Dealers Section */}
            {(selectedCategory === "overall" ||
              selectedCategory === "dealers") && (
              <div className="mb-3">
                <div className="justify-start !text-[#2D3643] text-base font-medium uppercase mb-3 mt-3">
                  Dealers
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {overviewDealersCardsData.map((card, index) => (
                    <div
                      className="p-4 rounded-md dark:bg-primaryBackground bg-white !dark:border-[#cccccc] !border-[#cccccc] border border-solid"
                      key={card.id}
                    >
                      <div className="flex gap-4 items-center dark:text-[#E9EFF7] text-others-table-header">
                        <card.icon />
                        <span className="text-others-header dark:text-header">
                          {card.label}
                        </span>
                      </div>
                      <div className="text-4xl mt-3 dark:text-header text-others-table-header">
                        {index === 1
                          ? "৳" +
                            formatSouthAsianNumber(Number(card.amount) || 0)
                          : formatSouthAsianNumber(Number(card.amount) || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Traders Section */}
            {(selectedCategory === "overall" ||
              selectedCategory === "traders") && (
              <div className="mb-3">
                <div className="justify-start !text-[#2D3643] text-base font-medium uppercase mb-3 mt-3">
                  Traders
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {overviewTreaderCardsData.map((card, index) => (
                    <div
                      className="p-4 rounded-md dark:bg-primaryBackground bg-white !dark:border-[#cccccc] !border-[#cccccc] border border-solid"
                      key={card.id}
                    >
                      <div className="flex gap-4 items-center dark:text-[#E9EFF7] text-others-table-header">
                        <card.icon />
                        <span className="text-others-header dark:text-header">
                          {card.label}
                        </span>
                      </div>
                      <div className="text-4xl mt-3 dark:text-header text-others-table-header">
                        {/* {index === 1
                          ? "৳" + (card.amount || 0)
                          : card.amount || 0} */}

                        {index === 1
                          ? "৳" +
                            formatSouthAsianNumber(Number(card.amount) || 0)
                          : formatSouthAsianNumber(Number(card.amount) || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Retailers Section */}
            {(selectedCategory === "overall" ||
              selectedCategory === "retailers") && (
              <div className="mb-3">
                <div className="justify-start !text-[#2D3643] text-base font-medium uppercase mb-3 mt-3">
                  Retailers
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {overviewRetailerCardsData.map((card, index) => (
                    <div
                      className="p-4 rounded-md dark:bg-primaryBackground bg-white !dark:border-[#cccccc] !border-[#cccccc] border border-solid"
                      key={card.id}
                    >
                      <div className="flex gap-4 items-center dark:text-[#E9EFF7] text-others-table-header">
                        <card.icon />
                        <span className="text-others-header dark:text-header">
                          {card.label}
                        </span>
                      </div>
                      <div className="text-4xl mt-3 dark:text-header text-others-table-header">
                        {/* {index === 1
                          ? "৳" + (card.amount || 0)
                          : card.amount || 0} */}

                        {index === 1
                          ? "৳" +
                            formatSouthAsianNumber(Number(card.amount) || 0)
                          : formatSouthAsianNumber(Number(card.amount) || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-6 items-start">
              <TransactionsChart
                dates={topDealersDate}
                selectedCategory={selectedCategory}
                selectedSupplier={selectedSupplier}
              />

              <div className="lg:col-span-1 w-full p-4 rounded-md bg-white dark:bg-primaryBackground !dark:border-[#cccccc] !border-[#cccccc] border border-solid">
                <div className="flex gap-2 my-2 justify-between items-center">
                  <div className="text-lg dark:text-header text-others-table-header">
                    {getTopTitle()}
                  </div>
                  {/* {selectedCategory === "overall" && (
                    <div className="h-10 px-1.5 rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-400 inline-flex justify-start items-center gap-2">
                  
                      <button
                        onClick={() => {
                          setActiveButton("dealers");
                        }}
                        className={`h-8 px-1.5 rounded-md flex justify-start items-center text-base font-medium cursor-pointer ${
                          activeButton === "dealers"
                            ? "bg-blue-600 text-white !border-none !hover:border-none !hover:text-white"
                            : "!bg-transparent !border-none !hover:bg-transparent !hover:border-none text-slate-700"
                        }`}
                      >
                        Dealers
                      </button>

               
                      <button
                        onClick={() => {
                          setActiveButton("traders");
                        }}
                        className={`h-8 px-1.5 rounded-md flex justify-start items-center text-base font-medium cursor-pointer ${
                          activeButton === "traders"
                            ? "bg-blue-600 text-white !border-none !hover:border-none !hover:text-white"
                            : "!bg-transparent !border-none !hover:bg-transparent !hover:border-none text-slate-700"
                        }`}
                      >
                        Traders
                      </button>

               
                      <button
                        onClick={() => {
                          setActiveButton("retailers");
                        }}
                        className={`h-8 px-1.5 rounded-md flex justify-start items-center text-base font-medium cursor-pointer ${
                          activeButton === "retailers"
                            ? "bg-blue-600 text-white !border-none !hover:border-none !hover:text-white"
                            : "!bg-transparent !border-none !hover:bg-transparent !hover:border-none text-slate-700"
                        }`}
                      >
                        Tetailers
                      </button>
                    </div>
                  )} */}

                  {selectedCategory === "overall" && (
                    <Select
                      className="min-w-[140px]"
                      suffixIcon={<SelectDownIcon />} // optional
                      value={activeButton}
                      onChange={(value) => setActiveButton(value)}
                      options={[
                        { value: "dealers", label: "Dealers" },
                        { value: "traders", label: "Traders" },
                        { value: "retailers", label: "Retailers" },
                      ]}
                      dropdownStyle={{
                        borderRadius: 8,
                      }}
                    />
                  )}
                </div>

                {getTopPartiesIsLoading ? (
                  <Loader />
                ) : (
                  <>{renderTopContent()}</>
                )}
              </div>
            </div>
          </div>
        )}
      </SidebarLayout>
    </>
  );
}
